<template>
	<section v-if="tabs.length > 0" class="container" aria-label="Inspirace pro příští nákup">
		<h2 class="typo-h1 mb-2 md:mb-6 lg:mb-10 text-heading-primary">
			<template v-if="customTitle?.length">
				{{ customTitle }}
			</template>
			<template v-else>
				Inspirace pro příští nákup
			</template>
		</h2>
		<div class="flex items-start lg:justify-between lg:items-center gap-2 max-lg:flex-col">
			<nav
				class="overflow-x-auto md:mb-2 order-2 lg:order-1 max-lg:w-full"
				aria-label="Záložky s inspiracemi pro příští nákup"
			>
				<HorizontalScroll
					class="flex gap-2"
					role="tablist"
					fade-edges
				>
					<li v-for="(item, index) in tabs" :key="item.id" role="tab" class="inline-flex">
						<UiTabToggle
							:id="item.id"
							:as="AbsoluteInternalLink"
							:to="{hash: `#${item.id}`}"
							:aria-controls="item.id"
							:active="activeTabIndex === index"
							@click.prevent="handleClickTab(index)"
						>
							{{ item.label }}
						</UiTabToggle>
					</li>
				</HorizontalScroll>
			</nav>
			<InformationAboutHypermarket
				text="Informace o&nbsp;produktech se vztahují k&nbsp;pobočce"
				class="order-1 lg:order-2 md:shrink-0"
				:default-hm="DEFAULT_HYPERMARKET"
			/>
		</div>
		<div
			v-if="status === 'pending'"
			class="
				grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 my-8
				rem:auto-rows-[378px]
				*:rounded-2.5xl
				*:shadow-sm
				*:skeleton-gradient
				lg:[&:nth-child(n+4)]:*:hidden
				xl:[&:nth-child(n+4)]:*:block
			"
		>
			<div v-for="index in 4" :key="index" />
		</div>
		<div
			v-else-if="data?.products?.length"
			:id="`tabpanel-${currentTab.id}`"
			class="flex flex-col my-4 sm:my-8"
			role="tabpanel"
			:aria-labelledby="currentTab.id"
		>
			<div
				class="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-2 gap-4 sm:gap-6 md:gap-8"
			>
				<div
					v-for="(product, index) in data.products ?? []"
					v-show="index < pageSize"
					:key="product.vanr || index"
					class="contents"
				>
					<ProductTile
						:product="product"
						:product-category="currentProductCategory"
						:is-this-and-that-product="isRecommendationServiceUsed"
					/>
				</div>
			</div>
			<AbsoluteInternalLink
				:to="tabs[activeTabIndex].ctaLink"
				class="btn btn-primary btn-lg max-sm:w-full max-sm:justify-center mt-6 mx-auto md:mt-12"
			>
				Pokračovat
				<NuxtIcon
					name="custom/arrow-right"
					filled
					class="text-2xl"
				/>
			</AbsoluteInternalLink>
		</div>
		<div v-else class="text-center my-6">
			<NuxtIcon name="frown" filled class="text-7xl mb-4 text-icon-primary text-center inline-block" />
			<h4 class="typo-h4 text-heading-primary mb-6">
				Bohužel jsme nenašli žádné produkty
			</h4>
		</div>
	</section>
</template>

<script setup lang="ts">
	import {storeToRefs} from 'pinia';
	import {useStoryblokHypermarketsStore} from '~/stores/storyblokHypermarkets';
	import {useCustomerProfileStore} from '~/stores/customerProfile';
	import ProductCatalogServiceFactory from '~/services/productCatalog/ProductCatalogServiceFactory';
	import ProductCatalogService from '~/services/productCatalog/ProductCatalogService';
	import useProductCatalogServiceSwitcher from '~/services/productCatalog/useProductCatalogServiceSwitcher';
	import {MYGLOBUSPRICES, THISANDTHAT, DEFAULT_HYPERMARKET} from '#shared/constants';

	type Props = {
		customTitle?: string;
	};

	type Tab = {
		id: string;
		label: string;
		ctaLink: string;
		getProductCatalogService: () => Promise<ProductCatalogService | null>;
		isRecommendation?: boolean;
	};

	defineProps<Props>();

	const routeHash = useRouteHash();
	const currentBreakpoint = useCurrentBreakpoint();

	const AbsoluteInternalLink = resolveComponent('AbsoluteInternalLink');

	const activeTabIndex = ref(0);

	const customerProfileStore = useCustomerProfileStore();
	const hypermarketsStore = useStoryblokHypermarketsStore();

	const {selectedHypermarketWithFallbackInstance} = storeToRefs(hypermarketsStore);
	const {userIsLogged} = storeToRefs(customerProfileStore);

	const houseNumber = computed(() => selectedHypermarketWithFallbackInstance.value?.content.gsoaId);

	const pageSize = computed(() => currentBreakpoint.value === 'lg' ? 6 : 8);

	const branchSpecificPath = useBranchSpecificPath();

	const tabs = computed<Tab[]>(() => [
		userIsLogged.value
			? {
				id: THISANDTHAT.slug,
				label: THISANDTHAT.label,
				ctaLink: branchSpecificPath(`/hypermarket/akcni-nabidka/${THISANDTHAT.slug}`).value,
				getProductCatalogService: () => ProductCatalogServiceFactory.getRecommendationService({fallback: ProductCatalogServiceFactory.getMyGlobusBonusProgramPriceService}),
				isRecommendation: true,
			}
			: null,
		{
			id: MYGLOBUSPRICES.slug,
			label: MYGLOBUSPRICES.label,
			ctaLink: branchSpecificPath(`/hypermarket/akcni-nabidka/${MYGLOBUSPRICES.slug}`).value,
			getProductCatalogService: userIsLogged.value
				? () => ProductCatalogServiceFactory.getRecommendationService({
					loadStrategy: 'bonus',
					fallback: ProductCatalogServiceFactory.getMyGlobusBonusProgramPriceService,
				})
				: ProductCatalogServiceFactory.getMyGlobusBonusProgramPriceService,
			isRecommendation: userIsLogged.value,
		},
		{
			id: 'z-akcni-nabidky',
			label: 'Z akční nabídky',
			ctaLink: branchSpecificPath('/hypermarket/akcni-nabidka').value,
			getProductCatalogService: ProductCatalogServiceFactory.getActionOfferService,
		},
	].filter(nonNullable));

	const currentTab = computed(() => tabs.value[activeTabIndex.value]);
	const currentProductCategory = computed(() => {
		const currentTabId = tabs.value[activeTabIndex.value].id;
		return (currentTabId === MYGLOBUSPRICES.slug || currentTabId === THISANDTHAT.slug) ? currentTabId : undefined;
	});

	const isRecommendationServiceUsed = computed(() => {
		return currentTab.value?.isRecommendation === true;
	});

	const productCatalogServiceFactory = computed(() => currentTab.value.getProductCatalogService);

	const serviceSwitcher = useProductCatalogServiceSwitcher(productCatalogServiceFactory);

	const {data, status} = await useAsyncData(
		'BranchProductListing',
		async () => {
			if (!houseNumber.value) {
				return ProductCatalogService.emptyResponse();
			}

			return await cancelTimeout(async () => {
				const productCatalogService = await serviceSwitcher.getService();

				return productCatalogService?.getProducts({
					houseNumber: Number(houseNumber.value),
					pageSize: pageSize.value,
					offset: 0,
				}) ?? null;
			});
		},
		{watch: [houseNumber, currentTab]},
	);

	// Reset activeTabIndex when tabs change and the current active tab is not available anymore
	watch(tabs, (newTabs) => {
		if (activeTabIndex.value >= newTabs.length) {
			activeTabIndex.value = 0;
		}
	});

	// Sync activeTab with route hash changes - Assistive24 chrome extension changes hash on tab click
	watch(routeHash, (newHash) => {
		const hashTab = newHash && tabs.value.find((item) => `#${item.id}` === newHash);

		if (hashTab) {
			const newIndex = tabs.value.findIndex((item) => item.id === hashTab.id);
			if (newIndex !== -1) {
				activeTabIndex.value = newIndex;
			}
		}
	});

	function handleClickTab(index: number) {
		navigateTo({hash: `#${tabs.value[index].id}`});
		activeTabIndex.value = index;
	}
</script>
